import { config } from '../config';
import { getIdToken } from './auth';
import { QueryRequest, QueryResponse, AuditQueryResponse, HealthResponse, SummaryResponse } from '../types';

/**
 * API client for Hallway backend.
 * All authenticated endpoints inject the Cognito ID token.
 */

async function authFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${config.apiEndpoint}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`API error ${response.status}: ${body}`);
  }

  return response;
}

/** POST /query — submit a question */
export async function submitQuery(request: QueryRequest): Promise<QueryResponse> {
  const response = await authFetch('/query', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.json();
}

/** GET /audit — retrieve audit logs (reviewer only) */
export async function getAuditLogs(params: {
  startDate: string;
  endDate: string;
  userId?: string;
  limit?: number;
  nextToken?: string;
}): Promise<AuditQueryResponse> {
  const searchParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
  });
  if (params.userId) searchParams.set('userId', params.userId);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.nextToken) searchParams.set('nextToken', params.nextToken);

  const response = await authFetch(`/audit?${searchParams}`);
  return response.json();
}

/** GET /health — no auth required */
export async function getHealth(): Promise<HealthResponse> {
  const response = await fetch(`${config.apiEndpoint}/health`);
  if (!response.ok) {
    throw new Error(`Health check failed: ${response.status}`);
  }
  return response.json();
}

/** GET /summary — authenticated (011 finding 3); returns pre-computed dashboard markdown */
export async function getSummary(): Promise<SummaryResponse> {
  const response = await authFetch('/summary');
  return response.json();
}
